'use client';

import type { Session } from 'next-auth';
import React, { useEffect, useState } from 'react';

import { Modal } from '@bytel/trilogy-react';

import {
    TradeInEligibilityContext,
    getPartnerProductTradeInQuestions,
    getPartnerProductTradeInQuote,
    submitTradeIn,
} from '@services/trade-in';

import { TradeInCriteriaResponsesType, TradeInCriteriaType, TradeInQuoteType } from '@app-types/trade-in';
import { UserType } from '@app-types/user';

import { CLASSIC_TRADE_IN_SUB_TYPE } from '@constants/easy-trade-in';
import { TradeInStep } from '@constants/trade-in';

import {
    TradeInConfirmationInput,
    getTradeInStep,
    sendTradeInStep1Tracking,
    sendTradeInStep2Tracking,
    sendTradeInStep3Tracking,
    setTradeInStep,
} from '@product-detail/helpers';

import { MobileTradeInStep1 } from './mobile-trade-in-step1';
import { MobileTradeInStep2 } from './mobile-trade-in-step2';
import { MobileTradeInStep3 } from './mobile-trade-in-step3';

export type MobileTradeInModalProps = {
    isOpen: boolean;
    onClose: () => void;
    step: number;
    setStep: (step: number) => void;
    productName: string;
    tradeInBonus: number;
    tradeInBonusPlan?: string;
    isRenewal: boolean;
    session: Session | null;
    userContext: TradeInEligibilityContext;
};

export function MobileTradeInModal({
    session,
    isOpen = false,
    onClose,
    step,
    setStep,
    productName,
    tradeInBonus,
    tradeInBonusPlan,
    isRenewal,
    userContext,
}: Readonly<MobileTradeInModalProps>) {
    const [quote, setQuote] = useState<TradeInQuoteType | undefined>(undefined);
    const [questions, setQuestions] = useState<TradeInCriteriaType>([]);
    const [isLoadingQuote, setIsLoadingQuote] = useState(true);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
    const user = session?.user as UserType | null;
    const handleGetQuestions = (productId: string) => {
        setIsLoadingQuestions(true);
        getPartnerProductTradeInQuestions(productId, user).then((data) => {
            setIsLoadingQuestions(false);
            setQuestions(data);
        });
    };

    const handleGetQuote = async (
        productId: string,
        criteria: TradeInCriteriaResponsesType['questions'],
        abortController?: AbortController,
    ) => {
        setQuote(undefined);
        setIsLoadingQuote(true);
        try {
            const data = await getPartnerProductTradeInQuote(
                productId,
                { mode: 'STANDARD', criteria },
                user,
                abortController,
            );

            const conformity = await submitTradeIn(userContext, data.id, CLASSIC_TRADE_IN_SUB_TYPE);
            if (abortController?.signal.aborted) return;

            const quoteWithApiAmount = {
                ...data,
                estimatedRecoveryAmount: Math.abs(conformity?.estimatedValue ?? 0) || data.estimatedRecoveryAmount,
            };

            setQuote(quoteWithApiAmount);

            const tradeInStepConfirmation: TradeInConfirmationInput = {
                amount: quoteWithApiAmount.estimatedRecoveryAmount,
                id: data.id,
                questions: criteria,
            };
            setTradeInStep(TradeInStep.CONFIRMATION, tradeInStepConfirmation);
        } catch (error) {
            if (!abortController?.signal.aborted) {
                console.error('Failed to get mobile trade-in quote:', error);
            }
        } finally {
            setIsLoadingQuote(false);
        }
    };

    useEffect(() => {
        const product = getTradeInStep(TradeInStep.SEARCH);

        if (step === TradeInStep.FORM && questions.length <= 0) {
            handleGetQuestions(product!.id);
        }

        if (step === TradeInStep.CONFIRMATION && !quote) {
            const valueStep = getTradeInStep(TradeInStep.FORM);
            handleGetQuote(product!.id, valueStep!);
        }
    }, [step, questions, quote]);

    useEffect(() => {
        if (!isOpen) return;

        const product = getTradeInStep(TradeInStep.SEARCH);

        if (step === TradeInStep.SEARCH) {
            sendTradeInStep1Tracking(isRenewal);
        }

        if (step === TradeInStep.FORM && questions.length <= 0) {
            sendTradeInStep2Tracking(isRenewal, product?.name ?? '');
        }

        if (step === TradeInStep.CONFIRMATION && quote) {
            sendTradeInStep3Tracking(isRenewal, product?.name ?? '', quote.estimatedRecoveryAmount);
        }
    }, [step, questions, quote, isOpen]);

    return (
        <Modal active={isOpen} onClose={onClose} title='Reprise mobile'>
            <MobileTradeInStep1
                step={step}
                setStep={setStep}
                productName={productName}
                tradeInBonusPlan={tradeInBonusPlan}
                tradeInBonus={tradeInBonus}
                getQuestions={handleGetQuestions}
                user={user}
            />
            <MobileTradeInStep2
                step={step}
                setStep={setStep}
                closeModal={onClose}
                questions={questions}
                getQuote={handleGetQuote}
                isLoading={isLoadingQuestions}
                isLoadingQuote={isLoadingQuote}
            />
            <MobileTradeInStep3
                step={step}
                setStep={setStep}
                quote={quote}
                tradeInBonus={tradeInBonus}
                closeModal={onClose}
                isLoading={isLoadingQuote}
                isRenewal={isRenewal}
            />
        </Modal>
    );
}
